import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, AppBar, Toolbar, Typography, Box, Paper, Chip, CircularProgress } from "@mui/material";
import { Save, Eye, Rocket } from "lucide-react";
import { useFormBuilder } from "../../hooks/use-form-builder";
import FieldPalette from "./form-builder/field-palette";
import FormCanvas from "./form-builder/form-canvas";
import PropertiesPanel from "./form-builder/properties-panel";
import PreviewModal from "./form-builder/preview-modal";
import Sidebar from "../Layout/sidebar";
const DEMO_USER_ID = "demo-user-1";

export default function FormBuilder() {
  const { id } = useParams();
  console.log("Form ID:", id)

  const [showPreview, setShowPreview] = useState(false);
  const [selectedField, setSelectedField] = useState(null);

  const { data: existingForm, isLoading } = useQuery({
    queryKey: [`${process.env.REACT_APP_API_URL}/api/admin/dynamicform/forms`],
    enabled: !!id && id !== "new",
  });

  const {
    form,
    updateForm,
    addField,
    updateField,
    deleteField,
    saveForm,
    isSaving,
  } = useFormBuilder(existingForm || {
    name: "",
    title: "",
    description: "",
    fields: [],
    status: "draft",
    userId: DEMO_USER_ID,
  });

  const handleSave = async () => {
    await saveForm();
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const handlePublish = async () => {
    await updateForm({ status: "published" });
    await saveForm();
  };

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box display="flex" minHeight="100vh" bgcolor="#f8fafc">
      {/* Sidebar could go here if needed */}
      <Sidebar />

      <Box flex={1} display="flex" flexDirection="column">
        {/* Header */}
        <AppBar position="static" color="inherit" elevation={1}>
          <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6" fontWeight="bold">Form Builder</Typography>
              <Chip label={form.status || "draft"} size="small" color="default" />
            </Box>

            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                onClick={handlePreview}
                startIcon={<Eye size={16} />}
                data-testid="button-preview"
              >
                Preview
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={isSaving}
                startIcon={<Save size={16} />}
                data-testid="button-save"
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="contained"
                sx={{ bgcolor: "green.600", "&:hover": { bgcolor: "green.700" } }}
                onClick={handlePublish}
                disabled={isSaving}
                startIcon={<Rocket size={16} />}
                data-testid="button-publish"
              >
                Publish
              </Button>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box display="flex" flex={1} p={2} gap={2}>
          {/* Field Palette */}
          <Paper elevation={1} sx={{ width: 260, p: 2 }}>
            <FieldPalette onAddField={addField} />
          </Paper>

          {/* Form Canvas */}
          <Paper elevation={1} sx={{ flex: 1, p: 3 }}>
            <FormCanvas
              form={form}
              onUpdateForm={updateForm}
              onSelectField={setSelectedField}
              onDeleteField={deleteField}
              selectedFieldId={selectedField?.id}
            />
          </Paper>

          {/* Properties Panel */}
          <Paper elevation={1} sx={{ width: 300, p: 2 }}>
            <PropertiesPanel
              selectedField={selectedField}
              onUpdateField={updateField}
            />
          </Paper>
        </Box>
      </Box>

      {/* Preview Modal */}
      {showPreview && (
        <PreviewModal
          form={form}
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
        />
      )}
    </Box>
  );
}
